import { useCallback, useEffect, useState } from "react";
import cx from "classnames";
import { Box, TextField, Text, Em } from "@radix-ui/themes";
import styles from "./InlineTextField.module.scss";

interface InlineTextFieldProps {
  size?: "1" | "2" | "3" | "4" | "5";
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  icon?: React.ReactNode;
}

export function InlineTextField({
  size,
  value,
  onChange,
  placeholder,
  disabled,
  icon,
}: InlineTextFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value);

  const handleBlur = () => {
    setIsEditing(false);
    if (localValue !== value) {
      onChange(localValue || "");
    }
  };

  const handleFocus = useCallback(() => {
    if (disabled) {
      return;
    }

    setIsEditing(true);
  }, [disabled]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(event.target.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      setIsEditing(false);
      if (localValue !== value) {
        onChange(localValue || "");
      }
    }
  };

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const isEmpty = localValue == null || localValue === "";

  return (
    <Box
      onClick={handleFocus}
      className={cx(styles.InlineTextFieldContainer, {
        [styles.disabled]: disabled,
      })}
    >
      {isEditing ? (
        <TextField.Root
          placeholder={placeholder}
          type="text"
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          autoFocus
          className={cx(styles.InlineTextField, styles[`size-${size}`])}
        />
      ) : (
        <Text
          color={"gray"}
          truncate
          highContrast={!isEmpty}
          className={cx(styles.InlineTextFieldValue, styles[`size-${size}`])}
        >
          {!isEmpty ? localValue : <Em>{placeholder}</Em>}
          {icon}
        </Text>
      )}
    </Box>
  );
}
